import { useState } from 'react'
import { useExperiments, useExperimentStats, useApproveExperiment, useRejectExperiment, useBulkApproveExperiments, useGenerateHypotheses, persistenzBefundAusApprove, persistenzBefundAusBulk, type ExperimentStatus, type ExperimentType, type ManualAction, type PersistenzBefund } from '@/api/experiments'
import { Link } from 'react-router-dom'

const STATUS_BADGES: Record<ExperimentStatus, { label: string; cls: string }> = {
  proposed: { label: 'Vorgeschlagen', cls: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Genehmigt', cls: 'bg-blue-100 text-blue-800' },
  running: { label: 'Laufend', cls: 'bg-green-100 text-green-800 animate-pulse' },
  evaluating: { label: 'Auswertung', cls: 'bg-orange-100 text-orange-800' },
  evaluated: { label: 'Ausgewertet', cls: 'bg-gray-100 text-gray-700' },
  shadow: { label: 'Shadow', cls: 'bg-purple-100 text-purple-800' },
  cancelled: { label: 'Abgebrochen', cls: 'bg-gray-100 text-gray-500 line-through' },
  rolled_back: { label: 'Rollback', cls: 'bg-red-100 text-red-800' },
  auto_approved: { label: 'Auto', cls: 'bg-green-100 text-green-700' },
}

const TYPE_LABELS: Record<ExperimentType, string> = {
  bid_adjustment: 'Bid',
  keyword_promotion: 'Promotion',
  keyword_addition: 'Keyword+',
  keyword_pause: 'Pause',
  negative_addition: 'Negative',
  budget_adjustment: 'Budget',
}

export default function ExperimentsDashboard() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [selected, setSelected] = useState<Set<number>>(new Set())

  const filters: Record<string, string | number> = { page, limit: 25 }
  if (statusFilter) filters.status = statusFilter
  if (typeFilter) filters.experimentType = typeFilter

  const { data, isLoading } = useExperiments(filters)
  const { data: statsData } = useExperimentStats()
  const approveMut = useApproveExperiment()
  const rejectMut = useRejectExperiment()
  const bulkApproveMut = useBulkApproveExperiments()
  const generateMut = useGenerateHypotheses()

  const stats = statsData?.stats
  const experiments = data?.experiments ?? []
  const total = data?.total ?? 0

  // M20-UI (Scheibe F): Die Hinweisbloecke speisen sich aus BEIDEN Approve-Wegen dieser
  // Seite — dem Sammel-Knopf UND dem Einzel-„Approve" je Tabellenzeile (unten, ~199).
  //
  // Der Einzel-Approve war im ersten Wurf uebersehen: Sein Ergebnis wurde nirgends
  // ausgewertet, ein ueber ihn ausgeloester M20-Park blieb damit voellig unsichtbar —
  // dieselbe Luecke, die dieses Modul fuer den Bulk-Weg schliesst. Genau die Fehlerklasse
  // aus Scheibe E: nicht den einen Fundort fixen, sondern den KONSUMENTENKREIS
  // enumerieren. Auf dieser Seite sind es zwei; die Detailseite ist der dritte und traegt
  // ihren eigenen Block.
  // Befunde werden GESAMMELT, nicht ersetzt.
  //
  // Codex-Durchgang 4, Finding 1: Beide Approve-Wege teilen sich EINEN Hook je Seite. Eine
  // zweite Einzel-Genehmigung ueberschreibt `data` und `variables` der ersten — der erste
  // Befund verschwand, ohne dass der Operator ihn gesehen haben muss. Genau die Information,
  // die dieses Modul sichtbar machen soll, ging beim schnellen Durchklicken verloren.
  //
  // Der Sammler haelt sie fuer die Dauer der Seite. Er ist bewusst KEINE Persistenz — der
  // Befund bleibt fluechtig (D-A5), aber er ueberlebt wenigstens den naechsten Klick.
  //
  // Codex-Durchgang 5, Finding 1: Der Sammler war richtig, erreichte aber nicht jede
  // Antwort. `mutate(id, { onSuccess })` haengt den Callback an den GETEILTEN Observer des
  // Hooks; TanStack fuehrt bei mehreren Aufrufen nur den des zuletzt gestarteten aus
  // (`query-core/src/types.ts` ~1250, `mutationObserver.ts` ~132). Zwei schnelle Klicks
  // hintereinander — schneller als die erste Antwort — und der erste Befund entstand nie.
  // Gemessen vom Reviewer: bei zwei parallelen Aufrufen kam ausschliesslich der zweite an.
  // Der Test dazu war zu schwach: Er wartete nach dem ersten Klick auf dessen Block und
  // pruefte damit nur die SEQUENTIELLE Abfolge.
  //
  // Deshalb unten `mutateAsync()` je Klick: Der Promise gehoert dem einzelnen Aufruf und
  // ueberlebt das Umhaengen des Observers.
  const [gesammelt, setGesammelt] = useState<Map<number, ManualAction>>(new Map())

  // Pro-ID-Sperre gegen den Doppelklick auf DERSELBEN Zeile — bewusst nicht global:
  // `approveMut.isPending` gilt fuer den ganzen Hook, eine globale Sperre verhinderte
  // genau die parallelen Genehmigungen VERSCHIEDENER Experimente, fuer die es den
  // Sammler ueberhaupt gibt. Ein zweiter Aufruf fuer dieselbe ID waere ohnehin ein
  // ungueltiger Statusuebergang (`proposed -> approved` nur einmal) — der Server lehnt
  // ihn ab, aber ein vermeidbarer Schreibversuch gegen die Amazon-Kette bleibt es.
  const [laufend, setLaufend] = useState<Set<number>>(new Set())

  // Scheibe M: Persistenzwarnungen, EIGENER Sammler neben `gesammelt`.
  //
  // WARUM NICHT DIESELBE MAP: `gesammelt` traegt `ManualAction` mit einer ANDEREN
  // Fail-closed-Regel ('unsafe-to-retry' wird nie ueberschrieben). Zwei verschiedene
  // Konfliktregeln in eine Map zu zwingen waere stille Kopplung — gleiche Form,
  // getrennte Verantwortung.
  const [warnungen, setWarnungen] = useState<Map<number, PersistenzBefund>>(new Map())

  function warnungenAufnehmen(neue: Array<[number, PersistenzBefund]>) {
    if (neue.length === 0) return
    setWarnungen(prev => {
      const next = new Map(prev)
      for (const [id, befund] of neue) {
        // Fail-closed wie beim Diskriminator selbst: 'local-persistence' wird nie durch
        // 'status-reload' ersetzt — die Ursache, die sich NICHT von selbst aufloest,
        // bleibt stehen.
        //
        // ⚠️ DIESE ZEILE IST NICHT TEST-GEBUNDEN — gemessen (Probe P3b): Entfernt man
        // sie, bleiben alle Tests gruen. Der Grund ist kein fehlender Test, sondern
        // dass der Konflikt in Produktion nicht auftreten KANN: Er braeuchte eine
        // zweite Genehmigung derselben ID, und `approveExperiment()` erlaubt nur
        // `proposed -> approved` (ein zweiter Aufruf wird serverseitig abgelehnt).
        // Ein Test dafuer muesste eine Aufrufform erfinden, die es nicht gibt — genau
        // der Fehler, den Scheibe H zweimal gekostet hat. Die Zeile bleibt trotzdem:
        // Sie kostet nichts und haelt die Regel fest, falls spaeter ein Pfad dazukommt,
        // der dieselbe ID erneut meldet.
        if (next.get(id)?.art === 'local-persistence') continue
        next.set(id, befund)
      }
      return next
    })
  }

  function befundeAufnehmen(neue: Array<[number, ManualAction]>) {
    if (neue.length === 0) return
    setGesammelt(prev => {
      const next = new Map(prev)
      for (const [id, aktion] of neue) {
        // Fail-closed bei Konflikt: 'unsafe-to-retry' gewinnt und wird nie
        // ueberschrieben. Ein Experiment, fuer das einmal ein moeglicher Amazon-Write
        // gemeldet wurde, darf nicht durch einen spaeteren harmlosen Befund in die
        // ungefaehrliche Liste rutschen.
        if (next.get(id) === 'unsafe-to-retry') continue
        next.set(id, aktion)
      }
      return next
    })
  }

  // Nach Ursache getrennt: Die beiden Faelle verlangen VERSCHIEDENE Aussagen. Ein
  // Status-Reload heilt durch ein Neuladen, eine Verbuchungsluecke nicht — ein
  // gemeinsamer Text muesste fuer einen der beiden luegen (Codex-Endstand, Finding 1).
  const nachUrsache = (() => {
    const lokal: number[] = []
    const reload: number[] = []
    for (const [id, befund] of warnungen) {
      (befund.art === 'status-reload' ? reload : lokal).push(id)
    }
    return { lokal, reload }
  })()

  const geparkt = (() => {
    const retrySafe: number[] = []
    const unsafeToRetry: number[] = []
    for (const [id, aktion] of gesammelt) {
      if (aktion === 'retry-safe') retrySafe.push(id)
      else unsafeToRetry.push(id)
    }
    return { retrySafe, unsafeToRetry }
  })()

  function toggleSelect(id: number) {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Experimente</h1>
        <button
          onClick={() => generateMut.mutate(false)}
          disabled={generateMut.isPending}
          className="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
        >
          {generateMut.isPending ? 'Generiere...' : 'Hypothesen generieren'}
        </button>
      </div>

      {/* KPI-Leiste */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <KpiCard label="Laufend" value={stats.running} />
          <KpiCard label="Vorgeschlagen" value={stats.pending} />
          <KpiCard label="Erfolgsquote 30d" value={`${stats.successRate30d}%`} />
          <KpiCard label="Shadow-Trefferquote" value={`${stats.shadowAccuracy}%`} />
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-3">
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
          className="rounded-md border px-3 py-1.5 text-sm">
          <option value="">Alle Status</option>
          {Object.entries(STATUS_BADGES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1) }}
          className="rounded-md border px-3 py-1.5 text-sm">
          <option value="">Alle Typen</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        {selected.size > 0 && (
          <button
            onClick={() => {
              // M20-UI (Scheibe F): Die Auswahl wird beim Absenden geleert — und zwar
              // unabhaengig vom Ausgang.
              //
              // Hier stand zwischenzeitlich der Versuch, genau die 'retry-safe'-Menge
              // markiert zu LASSEN ("Arbeitsliste statt Protokoll"). GEMESSEN, nicht
              // vermutet: Das kann nicht funktionieren. Ein von M20 geparktes Experiment
              // steht auf `running`; Checkboxen gibt es unten aber nur fuer `proposed`,
              // und `approveExperiment()` erlaubt ausschliesslich `proposed → approved`.
              // Die Markierung waere unsichtbar, der Knopf zeigte trotzdem "1 genehmigen",
              // und ein Klick liefe in einen ungueltigen Statusuebergang, der die Auswahl
              // kommentarlos leert. Die Auswahl haengt am APPROVE-Pfad — ein geparktes
              // Experiment ist bereits genehmigt und braeuchte `/execute/`.
              //
              // Und diesen Weg gibt es nirgends: Einen "Erneut ausfuehren"-Knopf hat auch
              // die Detailseite nicht (er wurde entfernt, weil `/execute/` fuer `running`
              // immer 409 liefert — Begruendung in `frontend/src/api/experiments.ts` an
              // der Stelle, an der der Hook stand). Die Detailseite hilft hier ohnehin
              // nicht weiter: Ihr Verdikt liegt in deren eigenem Seitenzustand, der beim
              // Navigieren nicht mitkommt; das Experiment steht dann auf `running`
              // und traegt dort weder Approve-Knopf noch Verdikt. Ein durables,
              // serverseitig lesbares Verdikt waere die Loesung — Registerpunkt D-A5,
              // offen. Deshalb verweisen die Hinweisbloecke unten bewusst NICHT auf die
              // Detailseite, sondern sagen, dass der Befund fluechtig ist.
              //
              // Leeren ist zugleich fail-closed fuer den Fehlerfall: Bricht die Verbindung
              // ab, ist unbekannt, wie viele Eintraege der Server schon verarbeitet hat.
              // Eine stehen gebliebene Auswahl behauptete dort "gefahrlos erneut
              // absendbar" — und ein zweiter Klick waere der zweite Amazon-Write.
              // Dass der Knopf damit sofort verschwindet, erschwert den Doppelklick —
              // aber es schliesst ihn NICHT: Der Operator kann waehrend des laufenden
              // Absendens neu auswaehlen und erneut klicken. Dann greift dieselbe Falle
              // wie beim Einzel-Approve (Codex-Durchgang 5, Finding 1): Der geteilte
              // Observer fuehrt nur den Callback des zuletzt gestarteten Laufs aus, der
              // erste Befundsatz entstuende nie. Deshalb auch hier `mutateAsync()` — der
              // Promise gehoert dem einzelnen Lauf.
              const ids = [...selected]
              setSelected(new Set())
              bulkApproveMut
                .mutateAsync(ids)
                .then(antwort => {
                  befundeAufnehmen(
                    antwort.results
                      .filter((r): r is typeof r & { manualAction: ManualAction } => r.manualAction !== undefined)
                      .map(r => [r.id, r.manualAction]),
                  )
                  // Scheibe M: ALLE Eintraege durchgehen, nicht nur die mit
                  // `manualAction` — sonst gingen genau die Faelle verloren, in denen
                  // ausschliesslich die Verbuchung scheiterte.
                  warnungenAufnehmen(
                    antwort.results.flatMap(r => {
                      const befund = persistenzBefundAusBulk(r)
                      return befund ? [[r.id, befund] as [number, PersistenzBefund]] : []
                    }),
                  )
                })
                .catch(() => {})
            }}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
          >
            {selected.size} genehmigen
          </button>
        )}
      </div>

      {/* M20-UI (Scheibe F): zwei getrennte Bloecke, weil die beiden Ausgaenge
          verschieden gefaehrlich sind. Der Text beschreibt den Zustand und
          verspricht keinen Aufloesungsweg (D-A5 ist offen). */}
      {/* Zweite Ursache, eigene Aussage: Hier war die Verbuchung erfolgreich, nur das
          Nachladen scheiterte. Ein Neuladen der Seite ist deshalb genau der Weg — das
          Gegenteil dessen, was der Block darueber sagt. */}
      {nachUrsache.reload.length > 0 && (
        <div data-testid="persistenz-band-reload" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <span className="font-semibold">
            {nachUrsache.reload.length} bei Amazon bestätigt und verbucht — Status nicht nachgeladen.
          </span>{' '}
          Betroffen: {nachUrsache.reload.map(id => `#${id}`).join(', ')}.
          Der Schreibvorgang und seine lokale Verbuchung sind durch; nur das unmittelbare Nachladen des
          Status ist fehlgeschlagen. Die Anzeige dieser Zeilen kann deshalb veraltet sein — ein Neuladen
          der Seite holt den echten Stand.
          <details data-testid="persistenz-band-reload-diagnose" className="mt-2 border-t pt-2">
            <summary className="cursor-pointer text-xs opacity-80">
              Diagnose – keine Handlungsanweisung
            </summary>
            <ul className="mt-1 font-mono text-xs opacity-70">
              {nachUrsache.reload.map(id => <li key={id}>#{id}: {warnungen.get(id)!.text}</li>)}
            </ul>
          </details>
        </div>
      )}
      {geparkt.retrySafe.length > 0 && (
        <div data-testid="bulk-manual-action" className="rounded-md border border-orange-300 bg-orange-50 p-3 text-sm text-orange-900">
          <span className="font-semibold">{geparkt.retrySafe.length} genehmigt, aber nicht ausgeführt.</span>{' '}
          Betroffen: {geparkt.retrySafe.map(id => `#${id}`).join(', ')}.
          Der Amazon-Istwert weicht vom erwarteten Vorzustand ab; es wurde nachweislich nichts geschrieben.
          Diese Experimente stehen jetzt auf „laufend" und sind hier nicht mehr auswählbar. Notiere die
          Nummern: Nach dem Verlassen dieser Seite ist der Befund nicht mehr abrufbar. Prüfe den Zustand
          bei Amazon.
        </div>
      )}
      {/* Scheibe M (PERSIST-UI): Der Schreibvorgang bei Amazon ist durch, die lokale
          Nachverarbeitung nicht. Eigenes Band, weil das eine ANDERE Aussage ist als die
          beiden M20-Bloecke: Dort ist der Amazon-Ausgang unbekannt, hier ist er
          bestaetigt. Keine Handlung — es gibt keinen Betriebsvorgang dafuer (D-A5). */}
      {nachUrsache.lokal.length > 0 && (
        <div data-testid="persistenz-band" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <span className="font-semibold">
            {nachUrsache.lokal.length} bei Amazon bestätigt, aber lokal unvollständig verarbeitet.
          </span>{' '}
          Betroffen: {nachUrsache.lokal.map(id => `#${id}`).join(', ')}.
          Der Schreibvorgang bei Amazon wurde bestätigt; die lokale Verbuchung oder der
          Audit-Eintrag sind unvollständig. Die Anzeige kann deshalb veraltet sein. Notiere die Nummern:
          Nach dem Verlassen dieser Seite ist der Befund nicht mehr abrufbar. Prüfe den Status dieser
          Experimente; ein Neuladen der Seite behebt das nicht.
          <details data-testid="persistenz-band-diagnose" className="mt-2 border-t pt-2">
            <summary className="cursor-pointer text-xs opacity-80">
              Diagnose – keine Handlungsanweisung
            </summary>
            <ul className="mt-1 font-mono text-xs opacity-70">
              {nachUrsache.lokal.map(id => <li key={id}>#{id}: {warnungen.get(id)!.text}</li>)}
            </ul>
          </details>
        </div>
      )}
      {geparkt.unsafeToRetry.length > 0 && (
        <div data-testid="bulk-unsafe" className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
          <span className="font-semibold">
            {geparkt.unsafeToRetry.length} mit unbestätigtem Amazon-Ausgang — NICHT erneut ausführen.
          </span>{' '}
          Betroffen: {geparkt.unsafeToRetry.map(id => `#${id}`).join(', ')}.
          Ein Write kann bereits erfolgt sein; ein erneuter Versuch könnte ihn doppeln. Für diese
          Experimente gibt es hier bewusst keine Sammelaktion. Notiere die Nummern: Nach dem Verlassen
          dieser Seite ist der Befund nicht mehr abrufbar. Prüfe den Ist-Zustand bei Amazon.
        </div>
      )}

      {/* Tabelle */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-8 px-3 py-2"><input type="checkbox" disabled /></th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Typ</th>
              <th className="px-3 py-2 text-left">Hypothese</th>
              <th className="px-3 py-2 text-left">Risk</th>
              <th className="px-3 py-2 text-left">Erstellt</th>
              <th className="px-3 py-2 text-left">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Laden...</td></tr>
            ) : experiments.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Keine Experimente</td></tr>
            ) : experiments.map(exp => (
              <tr key={exp.id} className="border-t hover:bg-muted/30">
                <td className="px-3 py-2">
                  {exp.status === 'proposed' && (
                    <input type="checkbox" checked={selected.has(exp.id)} onChange={() => toggleSelect(exp.id)} />
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${STATUS_BADGES[exp.status]?.cls ?? ''}`}>
                    {STATUS_BADGES[exp.status]?.label ?? exp.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs font-mono">{TYPE_LABELS[exp.experimentType] ?? exp.experimentType}</td>
                <td className="max-w-xs truncate px-3 py-2">
                  <Link to={`/experiments/${exp.id}`} className="hover:underline">{exp.hypothesis}</Link>
                </td>
                <td className="px-3 py-2">
                  <span className={`text-xs ${exp.riskLevel === 'high' ? 'text-red-600 font-bold' : exp.riskLevel === 'medium' ? 'text-orange-600' : 'text-green-600'}`}>
                    {exp.riskLevel}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(exp.createdAt).toLocaleDateString('de-DE')}</td>
                <td className="px-3 py-2 space-x-1">
                  {exp.status === 'proposed' && (
                    <>
                      <button
                        onClick={() => {
                          // Die ID stammt aus dem KLICK, nicht aus der Antwort: Das Backend
                          // liefert auf einem dokumentierten Pfad `experiment: null` samt
                          // gesetztem `manualAction` (Status-Reload gescheitert,
                          // `experiments/routes.ts` ~242). Aus der Antwort gelesen, waere
                          // der Befund dort still verschwunden.
                          setLaufend(prev => new Set(prev).add(exp.id))
                          approveMut
                            .mutateAsync(exp.id)
                            .then(antwort => {
                              if (antwort.manualAction) befundeAufnehmen([[exp.id, antwort.manualAction]])

                              // Scheibe M: Die Warnung haengt NICHT an `manualAction` —
                              // eine Genehmigung kann voellig unauffaellig sein und
                              // trotzdem melden, dass die Verbuchung unvollstaendig blieb.
                              const befund = persistenzBefundAusApprove(antwort)
                              if (befund) warnungenAufnehmen([[exp.id, befund]])
                            })
                            // Fehler meldet der globale MutationCache-Toast (main.tsx).
                            // Hier nur abfangen, damit keine unbehandelte Rejection entsteht.
                            .catch(() => {})
                            .finally(() => setLaufend(prev => {
                              const next = new Set(prev)
                              next.delete(exp.id)
                              return next
                            }))
                        }}
                        disabled={laufend.has(exp.id)}
                        className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-700 hover:bg-blue-200 disabled:opacity-50"
                      >Approve</button>
                      <button onClick={() => rejectMut.mutate({ id: exp.id })} className="rounded bg-red-100 px-2 py-1 text-xs text-red-700 hover:bg-red-200">Reject</button>
                    </>
                  )}
                  {(exp.status === 'running' || exp.status === 'evaluated') && (
                    <Link to={`/experiments/${exp.id}`} className="rounded bg-gray-100 px-2 py-1 text-xs hover:bg-gray-200">Detail</Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 25 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{total} Experimente</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded border px-3 py-1 disabled:opacity-30">Zurück</button>
            <span className="px-2 py-1">Seite {page}</span>
            <button disabled={page * 25 >= total} onClick={() => setPage(p => p + 1)} className="rounded border px-3 py-1 disabled:opacity-30">Weiter</button>
          </div>
        </div>
      )}
    </div>
  )
}

function KpiCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
    </div>
  )
}
